import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { IContactDetail } from '../models/index';

/**
 * Service to manage contact data state for the contact dialog.
 * This allows parent components to pass contact data to the ContactDetailComponent
 * without relying on @Input bindings which can have timing issues with dialogs.
 */
@Injectable({
    providedIn: 'root'
})
export class ContactDialogService {
    private contactDataSubject = new BehaviorSubject<IContactDetail | null>(null);
    private contactRevisionIdSubject = new BehaviorSubject<string | null>(null);
    private companyTypeSubject = new BehaviorSubject<string | null>(null);
    
    /** Observable stream of contact data */
    contactData$ = this.contactDataSubject.asObservable();
    
    /** Observable stream of contact revision ID */
    contactRevisionId$ = this.contactRevisionIdSubject.asObservable();
    
    /** Observable stream of company type */
    companyType$ = this.companyTypeSubject.asObservable();
    
    /**
     * Set the contact data to be displayed in the dialog
     */
    setContactData(data: IContactDetail, contactRevisionId: string, companyType: string): void {
        this.contactDataSubject.next(data);
        this.contactRevisionIdSubject.next(contactRevisionId);
        this.companyTypeSubject.next(companyType);
    }
    
    /**
     * Clear all contact data (call when dialog closes)
     */
    clearContactData(): void {
        this.contactDataSubject.next(null);
        this.contactRevisionIdSubject.next(null);
        this.companyTypeSubject.next(null);
    }
    
    /**
     * Get current contact data synchronously
     */
    getCurrentContactData(): IContactDetail | null {
        return this.contactDataSubject.getValue();
    }
    
    /**
     * Get current contact revision ID synchronously
     */
    getCurrentContactRevisionId(): string | null {
        return this.contactRevisionIdSubject.getValue();
    }
    
    /**
     * Get current company type synchronously
     */
    getCurrentCompanyType(): string | null {
        return this.companyTypeSubject.getValue();
    }
}
